import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { IApplicationState } from 'Store/types'
import { useDispatch, useSelector } from 'react-redux'
import { getCarPriceEstimator } from '../../Actions/action'
import { setPrice } from '../../Actions/reducer'
import { useForm } from 'react-hook-form'
import { format } from 'react-string-format'
import CurrencyInput from 'Components/Form/Input/CurrencyInput'
import Icon from 'Components/Icon'
import { useGetInsuranceExpertFieldsCurrentUserQuery } from 'Services/api'

const GetPricePage = (props: { handleNext: () => void; handleBack: () => void }) => {
  const [t] = useTranslation()
  const { loaded, price, towingValue } = useSelector(
    (store: IApplicationState) => store.visits.newVisit.prices,
  )
  const [hasAccess, setHasAccess] = useState<boolean>(false)
  const vehicleAccessories = useSelector(
    (store: IApplicationState) => store.visits.newVisit.vehicleAccessories,
  )
  const { hasTrailer } = useSelector((store: IApplicationState) => store.visits.newVisit.carInfo)

  const { data: InsuranceExpertFields, isSuccess: isExpertSuccess } =
    useGetInsuranceExpertFieldsCurrentUserQuery()

  const dispatch = useDispatch()
  const {
    handleSubmit,
    setFocus,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({ mode: 'onChange' })

  const totalAccessoryValue = vehicleAccessories.data.reduce(
    (acc, accessory) => acc + (accessory.price || 0),
    0,
  )

  useEffect(() => {
    setFocus('price')
    if (price) setValue('price', formatNumber(String(price)))
    if (towingValue) setValue('towingValue', formatNumber(String(towingValue)))
    if (!loaded) dispatch(getCarPriceEstimator(1))
  }, [])

  const formatNumber = (num: any) => {
    if (!num) return ''
    return num
      .replace(/\D/g, '') // Remove non-digit characters
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',') // Add commas every 3 digits
  }
  const getCoverageLimit = (): number => {
    if (isExpertSuccess && InsuranceExpertFields?.length) {
      const insurance = InsuranceExpertFields.find(x => x.insuranceTypeCoreId === 44)
      if (insurance) {
        return insurance.coverageLimit == null ? 999999999999 : insurance.coverageLimit
      }
    }
    return 0
  }

  const totalValue = () => {
    const price = Number(String(watch('price')).replace(/,/g, '')) || 0
    const towingValue = hasTrailer ? Number(String(watch('towingValue') ?? 0).replace(/,/g, '')) : 0

    return price + towingValue + totalAccessoryValue
  }

  useEffect(() => {
    const price = Number(String(watch('price')).replace(/,/g, '')) || 0
    const towingValue = hasTrailer ? Number(String(watch('towingValue')).replace(/,/g, '')) : 0
    const insurance = InsuranceExpertFields?.find(x => x.insuranceTypeCoreId === 44)
    if (insurance) {
      const limit = insurance.coverageLimit ?? 999999999999
      setHasAccess(limit >= price + towingValue)
    } else {
      setHasAccess(false)
    }
  }, [watch('price'), watch('towingValue'), hasTrailer, InsuranceExpertFields])

  const submitForm = async (data: any) => {
    dispatch(
      setPrice({
        price: Number(String(data.price).replace(/,/g, '')),
        towingValue: hasTrailer ? Number(String(data.towingValue).replace(/,/g, '')) : 0,
        totalValue: totalValue(),
      }),
    )
    props.handleNext()
  }

  return (
    <React.Fragment>
      <div className="flex flex-col grow overflow-auto">
        <div className="px-6 pb-6">
          <div className="text-sm mb-8">{t('ENTER-THE-APPRAISED-VALUE-OF-THE-VEHICLE')}:</div>

          <form>
            <div className="space-y-4">
              <CurrencyInput
                inputMode="numeric"
                id="price"
                name="price"
                label={t('PRICE')}
                unitText={t('PRICE_UNIT')}
                control={control}
                errors={errors.price}
                rules={{
                  required: true,
                  min: 100000000,
                  max: getCoverageLimit(),
                }}
              />

              {hasTrailer && (
                <React.Fragment>
                  <CurrencyInput
                    inputMode="numeric"
                    id="towingValue"
                    name="towingValue"
                    label={t('TOWING_VALUE')}
                    unitText={t('PRICE_UNIT')}
                    control={control}
                    errors={errors.towingValue}
                    rules={{
                      required: true,
                      min: 0,
                      max: 100000000000,
                    }}
                  />
                </React.Fragment>
              )}

              <div className="group form-floating w-full relative">
                <input
                  className="form-text transition-all pl-11 peer"
                  style={{ paddingLeft: '40px' }}
                  dir="ltr"
                  value={totalAccessoryValue.toLocaleString()}
                  readOnly
                />
                <label
                  htmlFor="outline"
                  className="peer-focus:text-xs peer-focus:-top-[8px] peer-focus:text-gray-800 dark:peer-focus:text-white peer-placeholder-shown:text-lg peer-placeholder-shown:top-3"
                >
                  <span className="relative">{t('TOTAL_ACCESSORY_VALUE')}</span>
                </label>
                <div className="absolute left-3 top-3.5">{t('PRICE_UNIT')}</div>
              </div>

              <div className="group form-floating w-full relative">
                <input
                  className="form-text transition-all pl-11 peer"
                  dir="ltr"
                  style={{ paddingLeft: '40px' }}
                  value={totalValue().toLocaleString()}
                  readOnly
                />
                <label
                  htmlFor="outline"
                  className="peer-focus:text-xs peer-focus:-top-[8px] peer-focus:text-gray-800 dark:peer-focus:text-white peer-placeholder-shown:text-lg peer-placeholder-shown:top-3"
                >
                  <span className="relative">{t('TOTAL_VALUE')}</span>
                </label>
                <div className="absolute left-3 top-3.5">{t('PRICE_UNIT')}</div>
              </div>

              {totalValue() > getCoverageLimit() && (
                <div
                  id="alert-error"
                  className="flex items-center p-3 text-white rounded-xl bg-gray-800 dark:bg-gray-800"
                  role="alert"
                >
                  <div className="bg-error/[.16] text-error p-2 rounded-lg flex justify-center items-center">
                    <Icon name="ic_error" className="w-6 h-6" />
                  </div>
                  <span className="sr-only">error</span>
                  <div className="ms-3 text-sm font-semibold grow">
                    {format(t('COVERAGE_LIMIT_MESSAGE'), getCoverageLimit().toLocaleString())}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="cta">
        <div className="flex justify-between items-center gap-2">
          <button
            className="btn btn-warning btn-lg flex gap-2 items-center justify-center w-full"
            onClick={props.handleBack}
          >
            <Icon name="arrow-right" className="w-6 h-6" />
            {t('BACK')}
          </button>
          <button
            className="btn btn-primary btn-lg flex gap-2 items-center justify-center w-full"
            disabled={
              (Number(String(watch('price')).replace(/,/g, ''))
                ? Number(String(watch('price')).replace(/,/g, ''))
                : 0) == 0 || !hasAccess
            }
            onClick={handleSubmit(submitForm)}
          >
            {t('NEXT')}
            <Icon name="arrow-left" className="w-6 h-6" />
          </button>
        </div>
      </div>
    </React.Fragment>
  )
}

export default GetPricePage
